/**
 * Dropdown choices, in the shape the Storyblok schema asks for.
 *
 * `taskv2_dropdown_block.options` is a `bloks` field restricted to
 * `taskv2_dropdown_option` — this space's house pattern for a repeated
 * free-text value (cf. `flex_gated_splash_content_avatars_item.value`).
 *
 * That pattern is not a preference, it is the only shape Storyblok can edit.
 * A bare `string[]` stores fine but no field type declares one, so it renders
 * as an out-of-schema field the author cannot touch; the multi-select `options`
 * type does store `string[]`, but its choices are fixed in the component schema,
 * and these choices are per-tenant, per-task content. `bloks` is what is left.
 *
 * Reads stay lenient: stories written before this field existed hold the bare
 * `string[]`, and the preview must render them rather than fail.
 */

export const DROPDOWN_OPTION_COMPONENT = 'taskv2_dropdown_option';

export interface DropdownOptionBlok {
  component: string;
  _uid: string;
  value: string;
}

/**
 * Build the blok list for one dropdown's choices.
 *
 * `_uid` is Storyblok's identity for a nested blok — the editor keeps a row
 * selected across a save by it, and duplicates confuse it. It is derived from
 * the field code and position rather than randomly so the same authored content
 * serialises identically twice, which keeps tests and diffs honest; field codes
 * are unique within a completion UI, so the pair cannot collide.
 */
export function toOptionBloks(options: readonly string[] | undefined, fieldCode: string): DropdownOptionBlok[] {
  return (options ?? [])
    .map((o) => (o ?? '').trim())
    .filter(Boolean)
    .map((value, i) => ({ component: DROPDOWN_OPTION_COMPONENT, _uid: `${fieldCode}-option-${i + 1}`, value }));
}

/**
 * Read a dropdown's choices back out, whichever shape the story holds.
 *
 * Anything unrecognised reads as no choices rather than throwing — this runs in
 * the preview's render path, where the old `options.filter((o) => o && o.trim())`
 * threw `e.trim is not a function` the moment an entry was not a string and
 * replaced the whole preview with an error card.
 */
export function readOptionValues(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((o) => {
      if (typeof o === 'string') return o;
      if (o && typeof o === 'object' && typeof (o as { value?: unknown }).value === 'string') {
        return (o as { value: string }).value;
      }
      return '';
    })
    .map((s) => s.trim())
    .filter(Boolean);
}
